/**
 * AccountSupervisionPanel — read-mostly status panel surfacing the
 * /api/v1/accounts/supervision/status payload.
 *
 * Behaviour:
 *  - Renders nothing until the first load resolves (loading skeleton).
 *  - Compact summary by default; expandable per-account drilldown.
 *  - "Run Account Review" button calls POST /run with allow_auto_correct=false.
 *  - NEVER shows or accepts broker credentials.
 *  - When no run has happened yet, surfaces that explicitly instead of
 *    fabricating a "healthy" view.
 */
import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  ExternalLink,
  Loader2,
  Play,
  RefreshCw,
  Shield,
} from 'lucide-react';
import {
  SupervisionAccount,
  useAccountSupervision,
} from '../hooks/useAccountSupervision';
import { useViewerPortfolio } from '../hooks/useViewerPortfolio';
import { apiV1Base } from '../lib/apiConfig';
// ADV-SIM-ACCOUNT-CONNECTION-CLARITY-001 — plain-English connection/mode/trading clarity.
import { connectionStatus, modeLabel, tradingAllowed, tooltipFor } from '../lib/accountClarity';

const _CLARITY_TONE: Record<string, string> = {
  green: 'text-emerald-700 bg-emerald-500/10 border-emerald-500/40',
  amber: 'text-amber-700 bg-amber-500/10 border-amber-500/50',
  red: 'text-rose-700 bg-rose-500/10 border-rose-500/40',
  gray: 'text-slate-600 bg-slate-400/10 border-slate-400/40',
};

function fmtCurrency(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return 'never';
    const diffMs = Date.now() - dt.getTime();
    const minutes = Math.round(diffMs / 60000);
    if (Math.abs(minutes) < 60) return `${Math.abs(minutes)}m ago`;
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 48) return `${Math.abs(hours)}h ago`;
    const days = Math.round(hours / 24);
    return `${Math.abs(days)}d ago`;
  } catch {
    return 'never';
  }
}

function severityClass(code: string): string {
  switch (code) {
    case 'EMERGENCY':
    case 'RED':
    case 'STALE_ACCOUNT_EMERGENCY':
    case 'RISK_REVIEW_REQUIRED':
      return 'text-rose-600 bg-rose-500/10 border-rose-500/40';
    // ADV-AS-P0: trade-inactivity ("idle") is ADVISORY, not an emergency. Even a
    // CRITICAL-tier idle (>=72 market hours / many days) renders AMBER attention,
    // never red. Red (rose) is reserved for a genuine EMERGENCY / RISK RED breach.
    case 'CRITICAL':
    case 'STALE_ACCOUNT_CRITICAL':
      return 'text-amber-700 bg-amber-500/10 border-amber-500/50';
    case 'WARNING':
    case 'YELLOW':
    case 'STALE_ACCOUNT_WARNING':
    case 'REVIEW_OVERDUE':
    case 'NEVER_REVIEWED':
    case 'UNDEPLOYED_CAPITAL':
    case 'UNMANAGED_POSITIONS':
      return 'text-amber-600 bg-amber-500/10 border-amber-500/40';
    case 'HEALTHY':
    case 'GREEN':
    case 'CURRENT':
      return 'text-emerald-600 bg-emerald-500/10 border-emerald-500/40';
    default:
      return 'text-[var(--muted-text)] bg-[var(--card-border)]/30 border-[var(--card-border)]';
  }
}

interface AccountRowProps {
  account: SupervisionAccount;
  // WO9 hotfix prop drilling (2026-06-06) — passed from
  // AccountSupervisionPanel so AccountRow can gate the per-account
  // buying-power / daily-P&L Metric values.
  viewerHasOwnBroker: boolean;
}

function AccountRow({ account, viewerHasOwnBroker }: AccountRowProps) {
  const [expanded, setExpanded] = useState(false);
  const idleDays = account.idle_days;
  const idleLabel = idleDays !== null && idleDays !== undefined
    ? `${idleDays.toFixed(1)} days`
    : '—';
  return (
    <div
      data-testid={`supervision-account-${account.account_id_masked}`}
      className="border border-[var(--card-border)] rounded-xl p-3 bg-[var(--input-bg)]/40"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Shield size={14} className="text-sky-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-[12px] font-black text-[var(--app-text)] italic truncate">
              {account.account_label}
            </div>
            <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
              {account.account_id_masked} · {account.mode}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            data-testid={`supervision-badge-no-trade-${account.account_id_masked}`}
            title={tooltipFor(account.no_trade_status)}
            className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${severityClass(
              account.no_trade_status,
            )}`}
          >
            {account.no_trade_status}
          </span>
          <span
            data-testid={`supervision-badge-review-${account.account_id_masked}`}
            title={tooltipFor(account.review_status)}
            className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${severityClass(
              account.review_status,
            )}`}
          >
            {account.review_status.replace('_', ' ')}
          </span>
          <span
            data-testid={`supervision-badge-risk-${account.account_id_masked}`}
            title={tooltipFor('RISK_' + account.risk_status) || tooltipFor(account.risk_status)}
            className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${severityClass(
              account.risk_status,
            )}`}
          >
            RISK {account.risk_status}
          </span>
          <button
            type="button"
            data-testid={`supervision-toggle-${account.account_id_masked}`}
            onClick={() => setExpanded((v) => !v)}
            className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest px-2 py-1 rounded-md border border-[var(--card-border)] hover:bg-[var(--card-border)]/40 flex items-center gap-1"
          >
            {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            Detail
          </button>
        </div>
      </div>

      {/* ADV-SIM-ACCOUNT-CONNECTION-CLARITY-001 — compact "can I trust / act on this?" row:
          connection (data freshness), account mode (paper/sim/live), and trading-allowed. */}
      {(() => {
        const conn = connectionStatus(account);
        const mode = modeLabel(account);
        const trade = tradingAllowed(account);
        return (
          <div data-testid={`supervision-clarity-${account.account_id_masked}`} className="mt-2 flex items-center gap-1.5 flex-wrap">
            <span data-testid={`supervision-connection-${account.account_id_masked}`} data-connection={conn.label}
                  title={conn.detail}
                  className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${_CLARITY_TONE[conn.tone]}`}>
              {conn.label}
            </span>
            <span data-testid={`supervision-mode-${account.account_id_masked}`} data-mode={mode.label}
                  title={mode.label === 'Live' ? 'Live brokerage account.' : `${mode.label} account — not a live brokerage account; no live orders are placed.`}
                  className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${_CLARITY_TONE[mode.tone]}`}>
              {mode.label}
            </span>
            <span data-testid={`supervision-trading-${account.account_id_masked}`} data-trading-allowed="false"
                  title={trade.reason}
                  className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${_CLARITY_TONE.gray}`}>
              Trading: No
            </span>
          </div>
        );
      })()}

      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-[10px]">
        <Metric label="Last trade" value={fmtRelative(account.last_trade_at)} />
        <Metric label="Last review" value={fmtRelative(account.last_review_at)} />
        <Metric label="Idle" value={idleLabel} />
        {/* WO8 gate financial metrics (2026-06-05) — these are SYSTEM-owned
            sim-* account balances surfaced by the protective-manager
            supervision summary. They are real values for Adversyn's house
            accounts but they are NOT the demo user's broker data. We
            display them only when the viewer has a self-connected paper
            account so they cannot be mistaken for the user's own. When the
            viewer has no broker connection, render "—" instead. */}
        <Metric label="Buying power" value={viewerHasOwnBroker ? fmtCurrency(account.buying_power) : '—'} />
        <Metric label="Daily P/L" value={viewerHasOwnBroker ? `${fmtCurrency(account.daily_pnl)} (${fmtPct(account.daily_pnl_percent)})` : '—'} />
        <Metric label="Open positions" value={String(account.open_positions_count)} />
      </div>

      {account.alert_codes.length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <AlertTriangle size={10} className="text-amber-600 shrink-0" />
          {account.alert_codes.map((code) => (
            <span
              key={code}
              data-testid={`supervision-alert-${code}`}
              title={tooltipFor(code)}
              className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${severityClass(code)}`}
            >
              {code}
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-3 text-[10px] text-[var(--muted-text)]">
          {account.action_required.length > 0 && (
            <div>
              <div className="font-black uppercase tracking-widest text-[9px] mb-1">Action required</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {account.action_required.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}
          {account.actions_taken.length > 0 && (
            <div>
              <div className="font-black uppercase tracking-widest text-[9px] mb-1 text-emerald-600">Actions taken (paper)</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {account.actions_taken.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}
          {account.actions_blocked.length > 0 && (
            <div>
              <div className="font-black uppercase tracking-widest text-[9px] mb-1 text-amber-600">Actions blocked</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {account.actions_blocked.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}
          {account.positions.length > 0 && (
            <div>
              <div className="font-black uppercase tracking-widest text-[9px] mb-1">Open positions</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="text-[var(--muted-text)] text-left">
                      <th className="py-1 pr-2">Symbol</th>
                      <th className="py-1 pr-2">Qty</th>
                      <th className="py-1 pr-2">Entry</th>
                      <th className="py-1 pr-2">Current</th>
                      <th className="py-1 pr-2">Unrealized</th>
                      <th className="py-1 pr-2">%</th>
                      <th className="py-1 pr-2">Classification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.positions.map((p, i) => (
                      <tr key={i} className="border-t border-[var(--card-border)]/50">
                        <td className="py-1 pr-2 font-bold text-[var(--app-text)]">{p.symbol}</td>
                        <td className="py-1 pr-2">{p.qty ?? '—'}</td>
                        <td className="py-1 pr-2">{fmtCurrency(p.avg_entry_price)}</td>
                        <td className="py-1 pr-2">{fmtCurrency(p.current_price)}</td>
                        <td className="py-1 pr-2">{fmtCurrency(p.unrealized_pnl)}</td>
                        <td className="py-1 pr-2">{fmtPct(p.unrealized_pnl_percent)}</td>
                        <td className="py-1 pr-2">
                          <span className={`px-1.5 py-0.5 rounded border ${severityClass(p.classification)}`}>
                            {p.classification}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <div className="font-bold uppercase tracking-widest text-[9px] text-[var(--muted-text)]">{label}</div>
      <div className="font-black text-[var(--app-text)]">{value}</div>
    </div>
  );
}

export default function AccountSupervisionPanel() {
  const {
    status,
    liveTradingBlocked,
    loading,
    error,
    reportPath,
    refetch,
    runNow,
    running,
  } = useAccountSupervision();
  // WO8 — viewer-scoped gate. Demo users without a self-connected
  // paper broker see '—' for per-account buying power / daily P&L
  // so they aren't mistaken for the user's own balances.
  const _viewerPortfolio = useViewerPortfolio();
  const viewerHasOwnBroker = Boolean(_viewerPortfolio?.data?.paper_account_connected);
  const [collapsed, setCollapsed] = useState(false);

  const counts = status?.counts;
  const hasEmergency = useMemo(
    () => (status?.accounts ?? []).some((a) => a.no_trade_status === 'EMERGENCY' || a.risk_status === 'RED'),
    [status],
  );

  if (loading) {
    return (
      <div
        data-testid="supervision-panel-loading"
        className="border border-[var(--card-border)] rounded-2xl p-4 bg-[var(--input-bg)] flex items-center gap-3"
      >
        <Loader2 size={16} className="text-sky-600 animate-spin" />
        <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
          Loading account supervision…
        </span>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div
        data-testid="supervision-panel-error"
        className="border border-amber-500/40 rounded-2xl p-4 bg-amber-500/5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-600" />
          <span className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">
            Account supervision unavailable: {error}
          </span>
        </div>
        <button
          type="button"
          onClick={refetch}
          data-testid="supervision-panel-retry"
          className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-amber-500/40 hover:bg-amber-500/10 flex items-center gap-1"
        >
          <RefreshCw size={10} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="supervision-panel"
      className={`border ${hasEmergency ? 'border-rose-500/40' : 'border-[var(--card-border)]'} rounded-2xl bg-[var(--input-bg)]/50`}
    >
      <div className="flex items-center justify-between gap-3 p-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {hasEmergency ? (
            <AlertTriangle size={16} className="text-rose-600" />
          ) : (
            <CheckCircle2 size={16} className="text-emerald-600" />
          )}
          <div className="min-w-0">
            <div className="text-[12px] font-black text-[var(--app-text)] uppercase italic">
              Account Supervision
            </div>
            <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
              {status
                ? `Last run ${fmtRelative(status.finished_at)} · ${status.accounts.length} accounts · ${status.status}`
                : 'No supervision run on record yet.'}
              {liveTradingBlocked ? ' · LIVE TRADING BLOCKED' : ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {counts && (
            <>
              <CountChip label="EMERG" value={counts.by_no_trade_status?.EMERGENCY ?? 0} variant="red" />
              <CountChip label="CRIT" value={counts.by_no_trade_status?.CRITICAL ?? 0} variant="red" />
              <CountChip label="WARN" value={counts.by_no_trade_status?.WARNING ?? 0} variant="amber" />
              <CountChip label="OK" value={counts.by_no_trade_status?.HEALTHY ?? 0} variant="green" />
            </>
          )}
          {reportPath && (
            <a
              href="#"
              data-testid="supervision-report-link"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const resp = await fetch(`${apiV1Base()}/accounts/supervision/report`, {
                    credentials: 'same-origin',
                  });
                  if (!resp.ok) return;
                  const body = await resp.json();
                  const md = body?.data?.markdown ?? '';
                  const win = window.open('', '_blank');
                  if (win) {
                    win.document.write('<pre style="white-space:pre-wrap;font-family:ui-monospace,monospace;padding:1em">' +
                      md.replace(/[&<>]/g, (c: string) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!)) + '</pre>');
                    win.document.title = 'Account Supervision Report';
                  }
                } catch {/* ignore */}
              }}
              className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-[var(--card-border)] hover:bg-[var(--card-border)]/40 flex items-center gap-1"
            >
              <ExternalLink size={10} /> Report
            </a>
          )}
          <button
            type="button"
            onClick={runNow}
            disabled={running}
            data-testid="supervision-run-button"
            className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-sky-500/40 hover:bg-sky-500/10 flex items-center gap-1 disabled:opacity-50"
          >
            {running ? <Loader2 size={10} className="animate-spin" /> : <Play size={10} />}
            Run Account Review
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            data-testid="supervision-collapse-button"
            className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-[var(--card-border)] hover:bg-[var(--card-border)]/40 flex items-center gap-1"
          >
            {collapsed ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>
      </div>

      {!collapsed && status && (
        <div className="px-4 pb-4 space-y-3">
          {status.accounts.length === 0 ? (
            <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest flex items-center gap-2">
              <Eye size={10} /> No paper/simulation accounts discovered.
            </div>
          ) : (
            status.accounts.map((acc) => (
              <AccountRow key={acc.account_id_masked + acc.account_label} account={acc} viewerHasOwnBroker={viewerHasOwnBroker} />
            ))
          )}
          {status.errors.length > 0 && (
            <div className="text-[10px] font-bold text-amber-600 uppercase tracking-widest flex items-start gap-2">
              <AlertTriangle size={10} className="mt-0.5 shrink-0" />
              <div>
                Partial run: {status.errors.length} error(s). Latest report still written.
              </div>
            </div>
          )}
          {status.forex_no_trade_summary && (
            <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest flex items-center gap-2 pt-1 border-t border-[var(--card-border)]/40">
              <Clock size={10} /> Forex monitor:
              <span className="text-[var(--app-text)]">{String(status.forex_no_trade_summary.severity ?? 'unknown')}</span>
              <span>· referenced, not duplicated</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CountChip({ label, value, variant }: { label: string; value: number; variant: 'red' | 'amber' | 'green' }) {
  const cls =
    variant === 'red'
      ? 'border-rose-500/40 text-rose-600 bg-rose-500/10'
      : variant === 'amber'
        ? 'border-amber-500/40 text-amber-600 bg-amber-500/10'
        : 'border-emerald-500/40 text-emerald-600 bg-emerald-500/10';
  return (
    <span
      data-testid={`supervision-count-${label}`}
      className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${cls}`}
    >
      {label} {value}
    </span>
  );
}
